// =============================================
// Help Tooltip Component
// KipiKid - אייקון עזרה עם tooltip בריחוף
// =============================================

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle } from 'lucide-react';

interface HelpTooltipProps {
  text: string;
  iconSize?: number;
  iconColor?: string;
  maxWidth?: number;
}

export default function HelpTooltip({
  text,
  iconSize = 14,
  iconColor = '#9ca3af',
  maxWidth = 260,
}: HelpTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('top');
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setPosition(rect.top < 80 ? 'bottom' : 'top');
    }
  }, [isVisible]);

  return (
    <div
      ref={iconRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      <HelpCircle
        size={iconSize}
        color={iconColor}
        style={{ cursor: 'help', flexShrink: 0 }}
      />
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: position === 'top' ? 4 : -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: position === 'top' ? 4 : -4 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute',
              [position === 'top' ? 'bottom' : 'top']: 'calc(100% + 8px)',
              right: 0,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              lineHeight: '1.5',
              maxWidth: `${maxWidth}px`,
              whiteSpace: 'pre-wrap',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 50,
              direction: 'rtl',
              textAlign: 'right',
              pointerEvents: 'none',
            }}
          >
            {text}
            <div
              style={{
                position: 'absolute',
                [position === 'top' ? 'bottom' : 'top']: '-4px',
                right: '8px',
                width: '8px',
                height: '8px',
                backgroundColor: '#1f2937',
                transform: 'rotate(45deg)',
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
